import { useState, useMemo } from 'react'
import { Card, CardBody, Input } from '@heroui/react'
import { Search, ShieldAlert, Activity, AlertTriangle, BarChart3 } from 'lucide-react'
import type { AlertCategory } from '../../types'
import {
  ALERT_TEMPLATES,
  ALERT_CATEGORIES,
  getActiveTemplateCount,
  getCriticalActiveCount,
  getCoveragePercent,
} from '../../lib/alert-templates-data'
import { MetricCard } from '../analytics/MetricCard'
import { AlertCategories } from './AlertCategories'
import { TemplateCard } from './TemplateCard'

export function AlertTemplates() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<AlertCategory | null>(null)
  const [templates, setTemplates] = useState(ALERT_TEMPLATES)

  const filteredTemplates = useMemo(() => {
    const query = searchQuery.toLowerCase()
    return templates.filter((t) => {
      if (selectedCategory && t.category !== selectedCategory) return false
      if (query && !t.name.toLowerCase().includes(query) && !t.triggerDescription.toLowerCase().includes(query)) {
        return false
      }
      return true
    })
  }, [templates, searchQuery, selectedCategory])

  const handleToggleActive = (templateId: string, isActive: boolean) => {
    setTemplates((prev) =>
      prev.map((t) => (t.id === templateId ? { ...t, isActive } : t))
    )
  }

  const totalTemplates = templates.length
  const activeCount = getActiveTemplateCount()
  const criticalCount = getCriticalActiveCount()
  const coverage = getCoveragePercent()

  return (
    <div className="space-y-6 animate-fade-in">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Templates"
          value={totalTemplates}
          icon={<ShieldAlert className="w-6 h-6" />}
          color="var(--fn-primary)"
        />
        <MetricCard
          title="Active Alerts"
          value={activeCount}
          icon={<Activity className="w-6 h-6" />}
          color="#10b981"
        />
        <MetricCard
          title="Critical Active"
          value={criticalCount}
          icon={<AlertTriangle className="w-6 h-6" />}
          color="#ef4444"
        />
        <MetricCard
          title="Coverage"
          value={`${coverage}%`}
          icon={<BarChart3 className="w-6 h-6" />}
          color="#8b5cf6"
        />
      </div>

      {/* Categories */}
      <AlertCategories
        categories={ALERT_CATEGORIES}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      {/* Search */}
      <Card>
        <CardBody className="p-3">
          <Input
            startContent={<Search className="w-5 h-5 text-default-400" />}
            variant="bordered"
            placeholder="Search alert templates..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            size="sm"
          />
        </CardBody>
      </Card>

      {/* Template Cards */}
      <div className="space-y-3">
        {filteredTemplates.length === 0 ? (
          <Card>
            <CardBody className="p-8 text-center">
              <p className="text-default-500">No templates match your filters</p>
            </CardBody>
          </Card>
        ) : (
          filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onToggleActive={handleToggleActive}
            />
          ))
        )}
      </div>
    </div>
  )
}
